import { useState } from 'react';
import { api, type Camera } from '../api.js';
import { t } from '../i18n.js';

// One camera: a snapshot image plus monitoring and (for Presence) floodlight
// controls. `onChanged` asks the parent to refetch the camera list after a
// control action so displayed state stays in sync with Netatmo.
export function CameraCard({ camera, onChanged }: { camera: Camera; onChanged: () => void }) {
  const [bust, setBust] = useState(() => Date.now());
  const [busy, setBusy] = useState(false);
  const monitoringOn = camera.monitoring === 'on';

  const toggleMonitoring = async () => {
    setBusy(true);
    try {
      await api.setMonitoring(camera.id, monitoringOn ? 'off' : 'on');
      onChanged();
    } finally {
      setBusy(false);
    }
  };

  const pickFloodlight = async (mode: 'auto' | 'on' | 'off') => {
    setBusy(true);
    try {
      await api.setFloodlight(camera.id, mode);
      onChanged();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="camera-card">
      <div className="camera-image">
        {!camera.reachable ? (
          <div className="camera-placeholder">{t('camera.offline')}</div>
        ) : !monitoringOn ? (
          <div className="camera-placeholder">{t('camera.monitoring_off_notice')}</div>
        ) : (
          <img src={`${camera.snapshot_url}?t=${bust}`} alt={camera.name} />
        )}
      </div>
      <div className="camera-body">
        <div className="camera-name">{camera.name}</div>
        <div className="camera-actions">
          <button
            className="chip"
            disabled={!camera.reachable || !monitoringOn || busy}
            onClick={() => setBust(Date.now())}
          >
            {t('video.refresh')}
          </button>
          <button
            className={`chip${monitoringOn ? ' is-active' : ''}`}
            disabled={busy}
            onClick={() => void toggleMonitoring()}
          >
            {monitoringOn ? t('camera.monitoring_on') : t('camera.monitoring_off')}
          </button>
        </div>
        {camera.type === 'presence' && camera.floodlight && (
          <div className="camera-floodlight">
            <span className="camera-floodlight-label">{t('camera.floodlight')}</span>
            <div className="camera-actions">
              {(['auto', 'on', 'off'] as const).map((mode) => (
                <button
                  key={mode}
                  className={`chip${camera.floodlight === mode ? ' is-active' : ''}`}
                  disabled={busy}
                  onClick={() => void pickFloodlight(mode)}
                >
                  {t(`camera.floodlight_${mode}`)}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
